// =============================================================================
// NAD Main Article - Related products Swiper + scroll-in animations
// =============================================================================

import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';

Swiper.use([Pagination]);

// Use threshold 0 so long article bodies still intersect: with a high threshold,
// intersectionRatio is bounded by viewportHeight / elementHeight and can never
// reach the threshold on very tall .nad-main-article__content wrappers.
const ANIMATION_IO: IntersectionObserverInit = {
  root: null,
  rootMargin: '0px 0px -8% 0px',
  threshold: 0,
};

const CONTENT_STAGGER_MS = 70;
const SIDEBAR_STAGGER_MS = 70;
const PRODUCTS_STAGGER_MS = 72;

export class NADMainArticle {
  private container: HTMLElement;
  private swiper: Swiper | null = null;
  private animationObserver: IntersectionObserver | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.init();
    this.setupAnimations();
  }

  private init(): void {
    const swiperEl = this.container.querySelector<HTMLElement>('.nad-main-article__products-swiper');
    if (!swiperEl) return;

    this.swiper = new Swiper(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 16,
      breakpoints: {
        768: {
          slidesPerView: 4,
          spaceBetween: 24,
        },
      },
      pagination: {
        el: swiperEl.querySelector('.swiper-pagination') as HTMLElement,
        clickable: true,
      },
    });
  }

  private setupAnimations(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.revealAnimatedElementsImmediate();
      return;
    }

    this.animationObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || !this.animationObserver) {
          return;
        }
        const target = entry.target as HTMLElement;

        if (target.classList.contains('nad-main-article__hero--desktop')) {
          target.classList.add('animate-in');
        } else if (target.classList.contains('nad-main-article__content')) {
          this.animateContent(target, CONTENT_STAGGER_MS);
        } else if (target.classList.contains('nad-main-article__sidebar')) {
          this.animateSidebar(target, SIDEBAR_STAGGER_MS);
        } else if (target.classList.contains('nad-main-article__products')) {
          this.animateProducts(target, PRODUCTS_STAGGER_MS);
        }

        this.animationObserver.unobserve(target);
      });
    }, ANIMATION_IO);

    const desktopHero = this.container.querySelector('.nad-main-article__hero--desktop');
    const content = this.container.querySelector('.nad-main-article__content');
    const sidebar = this.container.querySelector('.nad-main-article__sidebar');
    const products = this.container.querySelector('.nad-main-article__products');

    if (desktopHero) {
      this.animationObserver.observe(desktopHero);
    }
    if (content) {
      this.animationObserver.observe(content);
    }
    if (sidebar) {
      this.animationObserver.observe(sidebar);
    }
    if (products) {
      this.animationObserver.observe(products);
    }
  }

  private getContentAnimationElements(content: HTMLElement): HTMLElement[] {
    const ordered: HTMLElement[] = [];
    const mobileHero = content.querySelector<HTMLElement>('.nad-main-article__hero--mobile');
    if (mobileHero) {
      ordered.push(mobileHero);
    }
    const chip = content.querySelector<HTMLElement>(':scope > .nad-main-article__chip');
    if (chip) {
      ordered.push(chip);
    }
    const title = content.querySelector<HTMLElement>('.nad-main-article__title');
    if (title) {
      ordered.push(title);
    }
    const excerpt = content.querySelector<HTMLElement>('.nad-main-article__excerpt');
    if (excerpt) {
      ordered.push(excerpt);
    }
    const divider = content.querySelector<HTMLElement>('.nad-main-article__divider');
    if (divider) {
      ordered.push(divider);
    }
    const body = content.querySelector<HTMLElement>('.nad-main-article__body');
    if (body) {
      ordered.push(body);
    }
    return ordered;
  }

  private animateContent(content: HTMLElement, staggerMs: number): void {
    this.getContentAnimationElements(content).forEach((el, index) => {
      window.setTimeout(() => {
        el.classList.add('animate-in');
      }, index * staggerMs);
    });
  }

  private animateSidebar(sidebar: HTMLElement, staggerMs: number): void {
    const heading = sidebar.querySelector<HTMLElement>('.nad-main-article__related-heading');
    const cards = sidebar.querySelectorAll<HTMLElement>('.nad-main-article__card');
    let offset = 0;

    if (heading) {
      window.setTimeout(() => heading.classList.add('animate-in'), offset);
      offset += staggerMs;
    }

    cards.forEach((card, index) => {
      window.setTimeout(() => {
        card.classList.add('animate-in');
      }, offset + index * staggerMs);
    });
  }

  private animateProducts(products: HTMLElement, staggerMs: number): void {
    const header = products.querySelector<HTMLElement>('.nad-main-article__products-header');
    const slides = products.querySelectorAll<HTMLElement>('.nad-main-article__products-slide');

    if (header) {
      header.classList.add('animate-in');
    }

    slides.forEach((slide, index) => {
      window.setTimeout(() => {
        slide.classList.add('animate-in');
      }, staggerMs + index * staggerMs);
    });
  }

  private revealAnimatedElementsImmediate(): void {
    this.container.querySelector('.nad-main-article__hero--desktop')?.classList.add('animate-in');
    const content = this.container.querySelector<HTMLElement>('.nad-main-article__content');
    if (content) {
      this.animateContent(content, 0);
    }
    const sidebar = this.container.querySelector<HTMLElement>('.nad-main-article__sidebar');
    if (sidebar) {
      this.animateSidebar(sidebar, 0);
    }
    const products = this.container.querySelector<HTMLElement>('.nad-main-article__products');
    if (products) {
      this.animateProducts(products, 0);
    }
  }

  public destroy(): void {
    this.animationObserver?.disconnect();
    this.animationObserver = null;

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  static initializeAll(): void {
    document.querySelectorAll<HTMLElement>('.nad-main-article').forEach((el) => {
      if (!(el as any)._nadMainArticleInstance) {
        (el as any)._nadMainArticleInstance = new NADMainArticle(el);
      }
    });
  }
}
